import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip as MuiTooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { UseTaskReturn } from "../../hooks/useTask";
import { IoCubeOutline, IoInformationCircle } from "react-icons/io5";
import { useEffect, useRef } from "react";
import { Tooltip } from "../tooltip/Tooltip";
import { v1 } from "uuid";

export type ClassicColumn<T, S = string> = {
    name: string;
    tableHeaderCellBuilder?: () => React.ReactNode;
    tableCellBuilder: (data: T) => React.ReactNode;
    sort?: { asc: S; desc: S };
    width?: number;
    tooltip?: string;
    cellTooltipBuilder?: (data: T) => string;
}

export type PinnedColumn<T, S = string> = ClassicColumn<T, S> & { width: number; pinned: true; }

export type Column<T, S = string> = ClassicColumn<T, S> | PinnedColumn<T, S>;

export type TaskReturnValue<T> = {
    items: T[];
    count: number;
}

export type QueryRequiredFields = {
    page: number;
    limit?: number;
    order?: string | null;
}

export type DataTableProps<T, Q extends QueryRequiredFields> = {
    columns: Column<T>[];
    tableMinWidth: number;
    cellsHeight?: number;
    getDataTask: UseTaskReturn<[Q], TaskReturnValue<T>>;
    query: Q;
    setQuery: React.Dispatch<React.SetStateAction<Q>>;
    tableLoaderBuilder?: () => React.ReactNode;
    emptyTableBuilder?: () => React.ReactNode;
    paddingLeft?: number;
    onRowDoubleClick?: (row: T) => void;
    showLoaderOnlyOnInitialCall?: boolean;
    runOnlyWhenQueryChanges?: boolean;
    abortController?: AbortController;
}

export const isClassicColumn = <T,>(column: Column<T>): column is ClassicColumn<T> => {
    return "pinned" in column === false;
};

export const isPinnedColumn = <T,>(column: Column<T>): column is PinnedColumn<T> => {
    return "pinned" in column === true && column.width !== undefined;
};

export const DataTable = <T, Q extends QueryRequiredFields>(props: DataTableProps<T, Q>) => {
    const {
        tableMinWidth,
        getDataTask,
        tableLoaderBuilder,
        emptyTableBuilder,
        query,
        setQuery,
        runOnlyWhenQueryChanges,
        abortController
    } = props;

    const mounted = useRef<boolean>(false);

    const theme = useTheme();

    const { isRunning, perform, value: data, isInitialCall } = getDataTask;

    const showLoader = (props.showLoaderOnlyOnInitialCall && isRunning && isInitialCall) ||
        (!props.showLoaderOnlyOnInitialCall && isRunning);

    const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        if (runOnlyWhenQueryChanges && mounted.current) {
            perform(query);
        }

        if (!runOnlyWhenQueryChanges) {
            perform(query);
        }

        if (!mounted.current) mounted.current = true;

        return () => {
            abortController?.abort();
        }
    }, [query]);

    const rows = data?.items || [];

    const columns: ClassicColumn<T>[] = props.columns.filter(isClassicColumn);

    const pinnedColumns: PinnedColumn<T>[] = props.columns.filter(isPinnedColumn);

    if (showLoader) return <TableLoader tableLoaderBuilder={tableLoaderBuilder} />;

    if (!showLoader && rows.length === 0) return <EmptyTable emptyTableBuilder={emptyTableBuilder} />;

    return <TableContainer sx={{ height: "100%", maxHeight: "100%", pl: props.paddingLeft || 2 }}>
        <Table stickyHeader sx={{ minWidth: tableMinWidth }}>
            <TableHead sx={{
                position: "sticky",
                left: 0,
                zIndex: "100",
            }}>
                <TableRow>
                    {[...columns, ...pinnedColumns].map((column: Column<T>, index: number) => {
                        const isPinned = isPinnedColumn(column);

                        const pinnedStyles = isPinned ? {
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                        } : {};

                        if (column.tableHeaderCellBuilder) {
                            return <TableCell
                                key={index}
                                height={props.cellsHeight}
                                sx={{ ...pinnedStyles, backgroundColor: theme.palette.background.default }}
                            >
                                {column.tableHeaderCellBuilder()}
                            </TableCell>
                        }

                        return <DataTableCell
                            key={index}
                            column={column}
                            query={query}
                            setQuery={setQuery}
                            index={index}
                            cellsHeight={props.cellsHeight}
                        />
                    })}
                </TableRow>
            </TableHead>

            {!showLoader && rows.length === 0 ? <EmptyTable emptyTableBuilder={emptyTableBuilder} /> : null}

            {!showLoader && rows.length > 0 && <TableBody>
                {rows.map((row: T) => {
                    return <TableRow
                        key={v1()}
                        hover={!!props.onRowDoubleClick}
                        sx={{ cursor: !!props.onRowDoubleClick ? "pointer" : "default" }}
                        onDoubleClick={() => props.onRowDoubleClick && props.onRowDoubleClick(row)}
                    >
                        {[...columns, ...pinnedColumns].map((column: Column<T>, columnIndex: number) => {
                            const paddingLeft = (onlySmallScreen && columnIndex === 0) ? 2 : 1;

                            const isPinned = isPinnedColumn(column);

                            const pinnedStyles = isPinned ? {
                                position: "sticky",
                                right: 0,
                                zIndex: 3,
                            } : {};

                            const tableCell = <TableCell
                                key={columnIndex}
                                height={props.cellsHeight}
                                width={column.width}
                                sx={{
                                    paddingLeft: `${theme.spacing(paddingLeft)}!important`,
                                    backgroundColor: theme.palette.background.default,
                                    ...pinnedStyles
                                }}
                            >
                                {column.tableCellBuilder(row)}
                            </TableCell>

                            if (!!column.cellTooltipBuilder && !onlySmallScreen) {
                                return <Tooltip key={v1()} mobileHeader={column.name} bodyBuilder={(mobileVersion: boolean) => {
                                    const textColor = mobileVersion ? theme.palette.text.primary : theme.palette.common.white;
                                    return <Typography variant="body2" sx={{ color: textColor, whiteSpace: 'pre-line' }}>
                                        {!!column.cellTooltipBuilder && column.cellTooltipBuilder(row)}
                                    </Typography>
                                }}>
                                    {tableCell}
                                </Tooltip>
                            }

                            return tableCell;
                        })}
                    </TableRow>
                })}
            </TableBody>}
        </Table>
    </TableContainer>
}

type DataTableCellProps<T, Q extends QueryRequiredFields> = {
    column: Column<T>;
    query: Q;
    index: number;
    cellsHeight?: number;
    setQuery: React.Dispatch<React.SetStateAction<Q>>;
}

const DataTableCell = <T, Q extends QueryRequiredFields>({ column, query, setQuery, index, cellsHeight }: DataTableCellProps<T, Q>) => {
    const theme = useTheme();

    const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const isPinned = isPinnedColumn(column);

    const pinnedStyles = isPinned ? {
        position: "sticky",
        right: 0,
        zIndex: 2,
    } : {};

    const resolveTooltip = (component: React.ReactElement) => {
        if (!column.tooltip) return component;

        return <MuiTooltip title={column.tooltip}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {component}
                <IoInformationCircle size={20} />
            </Box>
        </MuiTooltip>
    }

    if (column.sort) {
        const { sort } = column;

        const active = query.order === sort.asc || query.order === sort.desc;

        let direction: 'asc' | 'desc' | undefined;

        if (active) {
            direction = query.order === sort.asc ? 'asc' : 'desc';
        }

        return <TableCell
            key={index}
            height={cellsHeight}
            sx={{
                paddingLeft: (onlySmallScreen && index === 0) ? 2 : 1,
                ...pinnedStyles,
                backgroundColor: theme.palette.background.default
            }}
        >
            {resolveTooltip(<TableSortLabel
                active={active}
                direction={direction}
                onClick={() => {
                    setQuery({
                        ...query,
                        order: active && direction === 'asc' ? sort.desc : sort.asc
                    });
                }}
            >
                <Typography variant="body2" whiteSpace="nowrap" sx={{ paddingLeft: (onlySmallScreen && index === 0) ? 1 : 0 }} color={theme.palette.text.primary}>
                    <strong>{column.name}</strong>
                </Typography>
            </TableSortLabel>)}
        </TableCell>
    }

    return <TableCell
        key={index}
        height={cellsHeight}
        sx={{
            ...pinnedStyles,
            backgroundColor: theme.palette.background.default
        }}
    >
        {resolveTooltip(<Typography variant="body2" sx={{ paddingLeft: (onlySmallScreen && index === 0) ? 1 : 0 }} whiteSpace="nowrap" color={theme.palette.text.primary}>
            <strong>
                {column.name}
            </strong>
        </Typography>)}
    </TableCell>
}

type TableLoaderProps = {
    tableLoaderBuilder?: () => React.ReactNode;
};

const TableLoader = ({ tableLoaderBuilder }: TableLoaderProps) => {
    const theme = useTheme();

    if (tableLoaderBuilder) {
        return tableLoaderBuilder();
    }

    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: theme.spacing(2),
            py: theme.spacing(8),
            width: "100%",
            height: "100%"
        }}>
            <CircularProgress color="primary" />
        </Box>
    );
}

type EmptyTableProps = {
    emptyTableBuilder?: () => React.ReactNode;
}

const EmptyTable = ({ emptyTableBuilder }: EmptyTableProps) => {
    const theme = useTheme();

    if (emptyTableBuilder) {
        return emptyTableBuilder();
    }

    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: theme.spacing(2),
            py: theme.spacing(8)
        }}>
            <IoCubeOutline size={32} color={theme.palette.grey[600]} />

            <Typography variant="body2">
                <i>Nenašli sa žiadné dáta.</i>
            </Typography>
        </Box>
    );
}